import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useGetSummaryQuery } from "@/services/cav-offline-sync";
import { SummaryQuery } from "@/services/cav-offline-sync/types";
import { formatNumber } from "@/utils/formatToMoney";
import { AlertCircle, CheckCircle, Clock } from "lucide-react";

interface SummaryCardsProps {
  filters?: SummaryQuery;
}

interface StatusCardProps {
  title: string;
  value: number | undefined;
  isLoading: boolean;
  isError: boolean;
  colorClass: string;
  icon: React.ReactNode;
}

const StatusCard = ({
  title,
  value,
  isLoading,
  isError,
  colorClass,
  icon,
}: StatusCardProps) => (
  <Card className="bg-gray-50 border border-gray-200">
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
      <CardTitle className={`text-sm font-medium ${colorClass}`}>
        {title}
      </CardTitle>
      <div className={colorClass}>{icon}</div>
    </CardHeader>
    <CardContent>
      {isLoading ? (
        <Skeleton className="h-8 w-24" />
      ) : isError ? (
        <span className="text-2xl font-bold text-gray-400">-</span>
      ) : (
        <span className={`text-2xl font-bold ${colorClass}`}>
          {formatNumber(value ?? 0)}
        </span>
      )}
    </CardContent>
  </Card>
);

export function SummaryCards({ filters }: SummaryCardsProps) {
  const {
    data: summary,
    isLoading,
    isError,
  } = useGetSummaryQuery(filters ?? {});

  return (
    <div className="grid gap-4 md:grid-cols-3 mb-6">
      <StatusCard
        title="Pendentes"
        value={summary?.pending}
        isLoading={isLoading}
        isError={isError}
        colorClass="text-yellow-700"
        icon={<Clock className="h-5 w-5" />}
      />
      <StatusCard
        title="Com Erro"
        value={summary?.failed}
        isLoading={isLoading}
        isError={isError}
        colorClass="text-red-700"
        icon={<AlertCircle className="h-5 w-5" />}
      />
      <StatusCard
        title="Processadas"
        value={summary?.successful}
        isLoading={isLoading}
        isError={isError}
        colorClass="text-green-700"
        icon={<CheckCircle className="h-5 w-5" />}
      />
    </div>
  );
}
